import { PRIMARY_MARKETS, SECONDARY_MARKETS } from '@/constants/markets';
import leven from 'leven';
import {
  find,
  flatten as lodashFlatten,
  flow,
  get,
  isNil,
  toUpper,
  uniq,
  words,
} from 'lodash';
import { filters, filterLabels } from './constants';
import type { QueryKeyword } from '@/constants/industries';
import {
  PRIMARY_INDUSTRIES,
  SECONDARY_INDUSTRIES,
  TERTIARY_INDUSTRIES,
} from '@/constants/industries';

type RuleNumber = (string | number)[]; // ['rule_name', 'is_between', 0, 99]
type RuleBool = (string | boolean)[]; // ['rule_name', '=', true|false]
type RuleWow = (string | (string | string[] | number | boolean)[][])[]; // ['aor', [ ['country_name', 'in', ['US', 'AU']], ['exchange_symbol', 'in', ['NYSE','NasdaqGS']]] ] 🤯

export type ParsedRule = RuleNumber | RuleWow | RuleBool;

export function parseRules(rules: string): ParsedRule[] {
  try {
    if (!rules) return [];
    const parsedRules = JSON.parse(rules);
    const isRuleWrapper =
      Array.isArray(parsedRules) && parsedRules.every(Array.isArray);

    return isRuleWrapper
      ? parsedRules.filter((ruleTuple: unknown[]) => ruleTuple.length)
      : parsedRules;
  } catch (reason) {
    console.warn(reason);
    console.log('Attempted to JSON.parse: ', rules);
    return [];
  }
}

export const validateRulesString = (rules?: string | null) => {
  try {
    if (!rules) {
      return false;
    }
    const parsedRules = JSON.parse(rules);
    if (!Array.isArray(parsedRules[0])) {
      return false;
    }
    return true;
  } catch (reason) {
    return false;
  }
};

/** Types declaration */
// @todo: market entries as enum

interface FlatTree {
  [id: string]: {
    type: string;
    label: string;
    children: string[];
    parentId?: string;
  };
}

export interface IndustryTree {
  primary?: FlatTree;
  secondary?: FlatTree;
  tertiary?: FlatTree;
}

export /**
 *
 *  This function creates the data object for the searchable list and
 *  also contains the actual search function for keywords.
 *
 */
const initializeSearch = (
  ordered: string[],
  sources: (string[] | { [key: string]: QueryKeyword })[]
) => {
  const keywords = sources.map((keyword) => keyword[0]);

  const ignored = sources.map((word) => word[1]);

  const fuzzy = (stringA = '', stringB = '') => {
    if (!stringA || !stringB) return 0;
    if (stringA === stringB) return 1;
    const distance = leven(stringA.toLowerCase(), stringB.toLowerCase());
    const longest = Math.max(stringA.length, stringB.length);
    return (longest - distance) / longest;
  };

  const filterIgnoredWords = (ignoredWord: string[]) => (keyword: string) =>
    !ignoredWord.includes(keyword);

  const getKeywords =
    (ignoredWord: string[]) =>
    (entity: { [key: string]: QueryKeyword }, id: string) => {
      const { label } = entity[id];
      return words(label.toLowerCase())
        .filter(filterIgnoredWords(ignoredWord))
        .concat([id]);
    };

  const toObjectEntries = (objectKeywords: { [key: string]: QueryKeyword }) =>
    Object.entries(objectKeywords);

  const flatten = (entries: [[string, [string]]]) =>
    entries.map((entry) => entry[1]);

  const keywordsReducers =
    (
      keywordsList: { [key: string]: QueryKeyword }[],
      ignoredWords: string[][],
      loops = 0
    ) =>
    (acc: { [key: string]: QueryKeyword }, id: string) => {
      const primaryKeywords = getKeywords(ignoredWords[loops])(
        keywordsList[loops],
        id
      );
      const { children = [] } = keywordsList[loops][id];

      const childKeywords: string[] = flow([
        (keywordChildren) =>
          keywordChildren.reduce(
            keywordsReducers(keywordsList, ignoredWords, loops + 1),
            {}
          ),
        (toEntryKeywords) => Object.entries(toEntryKeywords),
        flatten,
        lodashFlatten,
      ])(children);
      return { ...acc, [id]: uniq([...primaryKeywords, ...childKeywords]) };
    };
  const toHashTable = (keywordsList: string[]) => {
    const reducer: any = keywordsReducers(keywords, ignored);
    return keywordsList.reduce(reducer, {});
  };

  const createKeywordsFromQuery =
    (query: string, scoreThreshold: number) =>
    (keywordsEntries: [[string, [string]]]) =>
      keywordsEntries.reduce(
        (primaryAcc: [number, string][], [id, entries]) => {
          const withScore = entries.reduce(
            (secondaryAcc: [number, string][], word): [number, string][] => {
              const matchScore = fuzzy(word, query);
              const isPassingThreshold = matchScore >= scoreThreshold;
              return isPassingThreshold
                ? [...secondaryAcc, [matchScore, id]]
                : secondaryAcc;
            },
            []
          );
          return [...primaryAcc, ...withScore];
        },
        []
      );

  const sortByScore = (results: [number, string][]) =>
    results.sort(([scoreA], [scoreB]) => scoreB - scoreA);

  const transformDataMapper =
    (index: number, entities: { [key: string]: QueryKeyword }[]) =>
    (
      id: string
    ): {
      id: string;
      label: string;
      children?: { id: string; label: string }[];
    } => {
      const { label, children = [] } = entities[index][id];
      const endOfList = index + 1 === entities.length;
      return {
        id,
        label,
        ...(endOfList
          ? {}
          : {
              children: children.map(transformDataMapper(index + 1, entities)),
            }),
      };
    };

  const transformData = (results: string[]) =>
    results.map(transformDataMapper(0, keywords));

  // Dear past self, why on earth did I do this?
  let singletonPristine: {
    id: string;
    label: string;
    children: { id: string; label: string }[];
  }[];
  const pristineResult = () => {
    singletonPristine = singletonPristine || transformData(ordered);
    return singletonPristine;
  };

  let singletonKeywords: [string, [string]][];
  const searchableKeywords = () => {
    singletonKeywords =
      singletonKeywords || flow([toHashTable, toObjectEntries])(ordered);
    return singletonKeywords;
  };

  const searchFor = (query: string, scoreThreshold = 0.5) =>
    query === ''
      ? pristineResult()
      : flow([
          createKeywordsFromQuery(query, scoreThreshold),
          sortByScore,
          flatten,
          uniq,
          transformData,
        ])(searchableKeywords());
  return { searchFor };
};

interface IDotsCustomOptions {
  start?: number;
  step?: number;
}

export // Helper function to create dots. d'oh'ts
const dots = (
  count: number,
  customOptions?: IDotsCustomOptions
): { [key: string]: string } => {
  const options = {
    start: 0,
    step: 1,
    ...customOptions,
  };
  return [...new Array(count)].reduce(
    (acc, current, index) => ({
      ...acc,
      ...(index % options.step === 0 ? { [options.start + index]: '' } : {}),
    }),
    {}
  );
};

export const parseNumberSafe = (value: unknown) => {
  let targetValue = value;
  if (typeof value === 'string') {
    targetValue = parseInt(value, 10);
  }
  if (typeof targetValue === 'number' && isFinite(targetValue))
    return targetValue;
  return 0;
};

export const getSnowflakeValueFromRule = (rules: ParsedRule[]) => {
  const snowflake = getValueFromRule(
    rules,
    [
      'value_score',
      'dividends_score',
      'future_performance_score',
      'health_score',
      'past_performance_score',
    ],
    2
  );
  return {
    valueScore: parseNumberSafe(get(snowflake, 'value_score', 0)),
    dividendScore: parseNumberSafe(get(snowflake, 'dividends_score', 0)),
    futurePerformanceScore: parseNumberSafe(
      get(snowflake, 'future_performance_score', 0)
    ),
    healthScore: parseNumberSafe(get(snowflake, 'health_score', 0)),
    pastPerformanceScore: parseNumberSafe(
      get(snowflake, 'past_performance_score', 0)
    ),
  };
};

const flatten = (
  previousValue: ParsedRule[] | [],
  currentValue: ParsedRule
): ParsedRule[] => {
  if (currentValue[0] && currentValue[0].toString() === 'aor') {
    return [...previousValue, ...(currentValue[1] as any)];
  }
  return previousValue;
};

const getValueRules = (keywords: string[], rules: ParsedRule[]) => {
  const pluckedRules = rules.reduce(flatten, []).filter((rule: ParsedRule) => {
    return keywords.some((word) =>
      rule[0]
        ? rule[0].toString().includes(word) && rule[1].toString() !== 'not_in'
        : false
    );
  });
  return getValueFromRule(pluckedRules, keywords, 2);
};

export const getMarkets = (rules: ParsedRule[]) => {
  const keywords = ['country_name', 'exchange_symbol'];
  const valueRules: {
    country_name?: string[];
    exchange_symbol?: string[];
  } = getValueRules(keywords, rules);

  return {
    countries: valueRules.country_name || [],
    exchangeSymbols: valueRules.exchange_symbol || [],
  };
};

export const getIndustries = (rules: ParsedRule[]) => {
  const keywords = [
    'primary_industry_id',
    'secondary_industry_id',
    'tertiary_industry_id',
  ];
  const valueRules = getValueRules(keywords, rules);
  return {
    primary: (valueRules.primary_industry_id as string[]) || [],
    secondary: (valueRules.secondary_industry_id as string[]) || [],
    tertiary: (valueRules.tertiary_industry_id as string[]) || [],
  };
};

const filtersEntries = Object.entries(filters).map(
  ([_, filterKey]) => filterKey as string
);

const filterRules = (parsedRules: ParsedRule[]) =>
  parsedRules.filter(([rule, isNotNull, value]) => {
    let isSpecialCase = false;
    if (rule === filters.marketcap && isNotNull === 'is_not_null') {
      isSpecialCase = true;
    }
    if (rule === filters.isFund && (value as boolean) === false) {
      isSpecialCase = true;
    }

    if (rule === filters.gridVisibleFlag && (value as boolean) === true) {
      isSpecialCase = true;
    }
    return filtersEntries.includes(rule as string) && !isSpecialCase;
  });

const mapFriendlyName =
  (friendlyName: boolean) => (parsedRules: ParsedRule[]) =>
    parsedRules.map(([rule = '']) => {
      const safeRule = rule.toString();
      return friendlyName && safeRule in filterLabels
        ? filterLabels[safeRule]
        : rule;
    });

/** Include ETFs & Funds filter */
const addIsFund = (parsedRules: ParsedRule[]) => {
  const isFundRule = findRule(filters.isFund, parsedRules);
  if (!isFundRule) {
    return parsedRules.concat([[filters.isFund, '=', true]]);
  }
  return parsedRules;
};

const checkAorFilter = (filterId: string) => (parsedRules: ParsedRule[]) =>
  parsedRules.map((n) => {
    if (n[0] === 'aor' && n[1][0][0] === filterId) {
      return n[1][0];
    }
    return n;
  });

const isAorFilters = flow([
  checkAorFilter(filters.debtEquity),
  checkAorFilter(filters.net_debt_to_equity),
]);

export const getFilters = (rules: ParsedRule[], friendlyName = true) =>
  flow([addIsFund, isAorFilters, filterRules, mapFriendlyName(friendlyName)])(
    rules
  );

export const createDetailString = (
  rules: string[],
  subheadingResolver: (count: number) => string,
  cut = 3
) => {
  const sliced = rules.slice(0, cut);
  return {
    heading: sliced
      .map((market, index, parentArray) =>
        parentArray.length - 2 === index
          ? `${market} and `
          : `${market}${parentArray.length - 1 !== index ? ', ' : ''}`
      )
      .join(''),
    subheading: subheadingResolver(Math.max(0, rules.length - sliced.length)),
  };
};

const getValueFromRule = (
  rules: ParsedRule[],
  properties: string[],
  index: number
): Record<string, number | string | boolean | string[]> =>
  rules.reduce(
    (acc, current) =>
      properties.includes(current[0] as string) && current[index] !== undefined
        ? { ...acc, [current[0] as string]: current[index] }
        : acc,
    {}
  );

export const convertIndustryIdToName = (
  industryId: string,
  industries: Record<
    string,
    {
      label: string;
    }
  >[] = [PRIMARY_INDUSTRIES, SECONDARY_INDUSTRIES, TERTIARY_INDUSTRIES]
) => {
  if (industries[0][industryId]) {
    return industries[0][industryId].label;
  } else if (industries[1][industryId]) {
    return industries[1][industryId].label;
  } else if (industries[2][industryId]) {
    return industries[2][industryId].label;
  }
  return '';
};

export const getMarketLabel = (marketId: string) => {
  const lowCaseMarketId = marketId.toLowerCase();
  if (lowCaseMarketId in PRIMARY_MARKETS) {
    if ('label' in PRIMARY_MARKETS[lowCaseMarketId]) {
      return PRIMARY_MARKETS[lowCaseMarketId].label;
    }
  } else if (lowCaseMarketId in SECONDARY_MARKETS) {
    return SECONDARY_MARKETS[lowCaseMarketId].label;
  }
  return '';
};

export const updateMarketRule = (
  selectedCountries: string[],
  selectedExchangeSymbol: string[],
  rules: ParsedRule[]
): ParsedRule[] => {
  const targetIndex: number = rules.findIndex(
    (rule) =>
      rule[0] === 'aor' &&
      (rule[1][0][0] === 'country_name' || rule[1][0][0] === 'exchange_symbol')
  );
  const formatForRule = (selectedCountries, selectedExchangeSymbol) => {
    const hasSelectedCountries = selectedCountries.length !== 0;
    const hasSelectedExchangeSymbol = selectedExchangeSymbol.length !== 0;
    return hasSelectedCountries || hasSelectedExchangeSymbol
      ? [
          [
            'aor',
            [
              ...(hasSelectedCountries
                ? [['country_name', 'in', selectedCountries]]
                : []),
              ...(hasSelectedExchangeSymbol
                ? [['exchange_symbol', 'in', selectedExchangeSymbol]]
                : []),
            ],
          ],
        ]
      : [];
  };
  return targetIndex !== -1
    ? [
        ...rules.slice(0, targetIndex),
        /* check if there are toggled countries or exchange */
        ...formatForRule(selectedCountries, selectedExchangeSymbol),
        /* */
        ...rules.slice(targetIndex + 1),
      ]
    : rules.concat(
        formatForRule(selectedCountries, selectedExchangeSymbol) as string[][]
      );
};

export const updateMarketRuleWithExclusion = (
  excludedExchangeSymbol: string[],
  rules: ParsedRule[]
): ParsedRule[] => {
  const targetIndex: number = rules.findIndex(
    (rule) => rule[0] === 'aor' && rule[1][0][0] === 'exchange_symbol'
  );
  const formatForRule = (excludedExchangeSymbol) => {
    const hasExcludedExchangeSymbol = excludedExchangeSymbol.length !== 0;
    return hasExcludedExchangeSymbol
      ? [
          [
            'aor',
            [
              ...(hasExcludedExchangeSymbol
                ? [['exchange_symbol', 'not_in', excludedExchangeSymbol]]
                : []),
            ],
          ],
        ]
      : [];
  };
  return targetIndex !== -1
    ? [
        ...rules.slice(0, targetIndex),
        /* check if there are toggled countries or exchange */
        ...formatForRule(excludedExchangeSymbol),
        /* */
        ...rules.slice(targetIndex + 1),
      ]
    : rules.concat(formatForRule(excludedExchangeSymbol) as string[][]);
};

export const updateIndustryRule = (
  primary: string[],
  secondary: string[],
  tertiary: string[],
  rules: ParsedRule[]
): ParsedRule[] => {
  const targetIndex: number = rules.findIndex(
    (rule) =>
      rule[0] === 'aor' &&
      (rule[1][0][0] === 'primary_industry_id' ||
        rule[1][0][0] === 'secondary_industry_id' ||
        rule[1][0][0] === 'tertiary_industry_id')
  );

  const formatForRule = (primary, secondary, tertiary) => {
    const hasPrimary = primary.length !== 0;
    const hasSecondary = secondary.length !== 0;
    const hasTertiary = tertiary.length !== 0;
    return hasPrimary || hasSecondary || hasTertiary
      ? [
          [
            'aor',
            [
              ...(hasPrimary ? [['primary_industry_id', 'in', primary]] : []),
              ...(hasSecondary
                ? [['secondary_industry_id', 'in', secondary]]
                : []),
              ...(hasTertiary
                ? [['tertiary_industry_id', 'in', tertiary]]
                : []),
            ],
          ],
        ]
      : [];
  };
  return targetIndex !== -1
    ? [
        ...rules.slice(0, targetIndex),
        ...formatForRule(primary, secondary, tertiary),
        ...rules.slice(targetIndex + 1),
      ]
    : rules.concat(formatForRule(primary, secondary, tertiary) as string[][]);
};

export const updateSortByRule =
  (sortBy: string) => (parsedRules: ParsedRule[]) => {
    const index = parsedRules.findIndex((rule) => rule[0] === 'order_by');
    const currentOrderBy = parsedRules[index] || [];
    return updateSorting(parsedRules, [
      'order_by',
      sortBy,
      currentOrderBy[2] ? currentOrderBy[2].toString() : 'market_cap',
    ]);
  };

export const updateSortDirectionRule =
  (direction: string) => (parsedRules: ParsedRule[]) => {
    const index = parsedRules.findIndex((rule) => rule[0] === 'order_by');
    const currentOrderBy = parsedRules[index] || [];
    return updateSorting(parsedRules, [
      'order_by',
      currentOrderBy[1] ? currentOrderBy[1].toString() : 'desc',
      direction,
    ]);
  };

const updateSorting = (
  parsedRules: ParsedRule[],
  updatedSortingRule: string[]
) => {
  const index = parsedRules.findIndex((rule) => rule[0] === 'order_by');
  return JSON.stringify([
    ...parsedRules.slice(0, index),
    ...parsedRules.slice(index + 1),
    updatedSortingRule,
  ]);
};

export const getSortingRule = (
  parsedRules: ParsedRule[]
): { sortBy: string; direction: string } | null => {
  const index = parsedRules.findIndex((rule) => rule[0] === 'order_by');
  const orderByRule = parsedRules[index] || [];
  return orderByRule !== undefined
    ? {
        sortBy: orderByRule[1] ? orderByRule[1].toString() : 'market_cap',
        direction: orderByRule[2] ? orderByRule[2].toString() : 'desc',
      }
    : null;
};

export const getParentForChildPrimaryMarket = (
  childMarketId: string
): string | null => {
  const parentMarketId = null;

  for (const key of Object.keys(PRIMARY_MARKETS)) {
    if (
      get(PRIMARY_MARKETS, [key, 'children'], [] as string[]).includes(
        childMarketId
      )
    )
      return key;
  }

  return parentMarketId;
};

export /**
 * Note: To be called once and the value should be re-used
 *
 * Reason:
 * Helper function to convert Industries to a Flattened Tree
 * As the operation is cubic in nature (O(n3)) it should be called sparsely
 * preferably in the constructor or in static method
 *
 * Once the construction of the Tree is complete,
 * Lookup for values is constant (O(1))
 *
 */
const getIndustryRelationTree = (industryList): IndustryTree => {
  const industryRelation: IndustryTree = {};

  for (const key of Object.keys(industryList[0])) {
    const primaryIndustry = {
      type: 'PRIMARY',
      label: industryList[0][key].label,
      children: industryList[0][key].children,
    };

    if (!industryRelation.primary) {
      industryRelation.primary = { [key]: primaryIndustry };
    } else industryRelation.primary[key] = primaryIndustry;

    industryList[0][key].children.forEach((secondaryId) => {
      if (!isNil(industryList[1][secondaryId])) {
        const secondaryIndustry = {
          type: 'SECONDARY',
          label: industryList[1][secondaryId].label,
          children: industryList[1][secondaryId].children,
          parentId: key,
        };

        if (!industryRelation.secondary) {
          industryRelation.secondary = { [secondaryId]: secondaryIndustry };
        } else industryRelation.secondary[secondaryId] = secondaryIndustry;

        industryList[1][secondaryId].children.forEach((tertiaryId) => {
          if (!isNil(industryList[2][tertiaryId])) {
            const tertiaryIndustry = {
              type: 'TERTIARY',
              label: industryList[2][tertiaryId].label,
              children: [],
              parentId: secondaryId,
            };

            if (!industryRelation.tertiary) {
              industryRelation.tertiary = { [tertiaryId]: tertiaryIndustry };
            } else industryRelation.tertiary[tertiaryId] = tertiaryIndustry;
          }
        });
      }
    });
  }

  return industryRelation;
};

export const removeMarketChildrenForSelectedCountries = (
  countries: string[],
  exchangeSymbols: string[]
): string[] => {
  let combinedMarkets: string[] = [...exchangeSymbols.map(toUpper)];

  countries.forEach((marketKey) => {
    if (PRIMARY_MARKETS[marketKey.toLowerCase()]) {
      const mappedChildren = PRIMARY_MARKETS[marketKey.toLowerCase()].children;
      if (typeof mappedChildren !== 'undefined')
        combinedMarkets = combinedMarkets.filter(
          (x) => mappedChildren.map(toUpper).indexOf(x) < 0
        );

      combinedMarkets.push(PRIMARY_MARKETS[marketKey.toLowerCase()].label);
    } else {
      combinedMarkets.push(marketKey);
    }
  });

  return combinedMarkets;
};

const hasSlug = (value: {
  label?: string;
  slug?: string;
}): value is { slug: string } => {
  return value?.slug !== undefined;
};

export const getIndustryIdFromSlug = (routeSlug?: string) => {
  if (!routeSlug) return '';
  let key = '';
  const primary = Object.entries(PRIMARY_INDUSTRIES);
  const secondary = Object.entries(SECONDARY_INDUSTRIES);
  const tertiary = Object.entries(TERTIARY_INDUSTRIES);
  [...primary, ...secondary, ...tertiary].forEach(([currentKey, value]) => {
    if (hasSlug(value) && value.slug === routeSlug) {
      key = currentKey;
    }
  });
  return key;
};

const findRule = (targetName: string, parsedRule: ParsedRule[]) =>
  find(parsedRule, ([ruleName]) => ruleName === targetName);

const addIsFundFalse = (parsedRules: ParsedRule[]) => {
  const ruleFound = findRule(filters.isFund, parsedRules);
  if (!ruleFound) {
    return parsedRules.concat([['is_fund', '=', false]]);
  }
  return parsedRules;
};

const removeIsFundTrue = (parsedRules: ParsedRule[]) =>
  parsedRules.filter((rule) => {
    const [ruleName, _, value] = rule;
    const isFundIsTrue = ruleName === filters.isFund && value;
    return !isFundIsTrue;
  });

const addGridVisibleFlag = (parsedRules: ParsedRule[]) => {
  return parsedRules.concat([[filters.gridVisibleFlag, '=', true]]);
};

export const removeNullableRecords = (parsedRules: ParsedRule[]) => {
  const criteria = findRule(filters.marketCapMovement7d, parsedRules);
  if (!criteria) {
    return parsedRules.concat([[filters.marketCapMovement7d, 'is_not_null']]);
  }
  return parsedRules;
};

export const applySortingAndFiltering = (
  directionParam,
  sortByParam,
  parsedRules: ParsedRule[]
) => {
  const directionRule = updateSortDirectionRule(directionParam)(parsedRules);

  const sortingRule = updateSortByRule(sortByParam)(
    JSON.parse(directionRule) as ParsedRule[]
  );

  const removedNullable = removeNullableRecords(
    JSON.parse(sortingRule) as ParsedRule[]
  );

  /** Prevent OTCPK exchange from being fetched */
  const excludedOtcpkRule = updateMarketRuleWithExclusion(
    ['OTCPK'],
    removedNullable
  );
  return excludedOtcpkRule;
};

export const fixRulePayload: (rule: string) => string = flow([
  parseRules,
  addIsFundFalse, // when the "Include ETFs & Funds" is not toggled we add a `['is_fund', '=', false]` rule.
  removeIsFundTrue, // when the "Include ETFs & Funds" is toggled we remove the `['is_fund', '=', true]` rule.
  addGridVisibleFlag, // Should Always only include stocks that are visible
  JSON.stringify,
]);

export function getKeywordsFromRules(rules?: string) {
  if (typeof rules === 'undefined') {
    return '';
  }
  const parsedRules = parseRules(rules);
  const keyword = getValueFromRule(parsedRules, ['description'], 2);
  return keyword;
}

export function setDescriptionRule(rules: string, description: string) {
  const parsedRules = parseRules(rules);
  const ruleIndex = parsedRules.findIndex((rule) => rule[0] === 'description');
  if (ruleIndex === -1 && description !== '') {
    // create 'description' entry in rules
    return JSON.stringify(
      parsedRules.concat([['description', 'like', description]])
    );
  }

  if (ruleIndex !== -1 && description === '') {
    // delete from rules if blank string
    // ⚠️ can delete the entire rules so be careful
    parsedRules.splice(ruleIndex, 1);
    return JSON.stringify(parsedRules);
  } else if (description !== '') {
    parsedRules[ruleIndex] = ['description', 'like', description];
    return JSON.stringify(parsedRules);
  } else {
    return JSON.stringify(parsedRules);
  }
}
